// COMPLETELY NEW AUTHSERVICE - BYPASSING ALL CACHE ISSUES
console.log('🔥🔥🔥 BRAND NEW AUTHSERVICE LOADED - NO CACHE - TIMESTAMP: ' + Date.now());
console.log('🔥🔥🔥 IF YOU SEE THIS MESSAGE, THE NEW CODE IS WORKING!');

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: {
    id: number;
    name: string;
    email: string;
    role: string;
    permissions?: Record<string, boolean>;
    forcePasswordChange?: boolean;
  };
  error?: string;
  message?: string;
  forcePasswordChange?: boolean;
}

interface RegisterResponse {
  success: boolean;
  message?: string;
  error?: string;
  details?: string[];
  user?: {
    id: number;
    name: string;
    email: string;
    role: string;
    permissions?: Record<string, boolean>;
  };
  token?: string;
}

interface RegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface AdminCheckResponse {
  success: boolean;
  isAdmin: boolean;
  role: string;
  permissions: Record<string, boolean>;
}

interface PasswordResetResponse {
  success: boolean;
  message?: string;
  error?: string;
}

interface ChangePasswordResponse {
  success: boolean;
  message?: string;
  error?: string;
}

class AuthService {
  private baseURL: string = `${process.env.REACT_APP_USER_SERVICE_URL}/api/auth`;
  private sessionManager: any = null; // Will be set during initialization

  /**
   * Initialize the auth service with session manager
   */
  public setSessionManager(sessionManager: any): void {
    this.sessionManager = sessionManager;
  }



  async login(email: string, password: string): Promise<LoginResponse> {
    console.log('🔥🔥🔥 BRAND NEW LOGIN METHOD EXECUTING - TIMESTAMP: ' + Date.now());
    console.log('🔥🔥🔥 THIS PROVES THE NEW AUTHSERVICE IS WORKING!');
    try {
      const response = await fetch(`${this.baseURL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      console.log('🔍 Raw login response data:', data);
      console.log('🔍 Response has success:', !!data.success);
      console.log('🔍 Response has token:', !!data.token);
      console.log('🔍 Response has user:', !!data.user);
      console.log('🔍 Data structure keys:', Object.keys(data));

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Login failed');
      }

      // Check if login was successful
      if (data.success) {
        console.log('✅ Login was successful, checking token and user...');
        
        if (data.token && data.user) {
          console.log('💾 Starting session with SessionManager...');
          
          // Store refresh token
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          
          // Use session manager if available, otherwise fallback to localStorage
          if (this.sessionManager) {
            this.sessionManager.startSession(data.token, data.user);
          } else {
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        } else {
          console.error('❌ Missing token or user data in response');
        }
        
        // Verify storage immediately
        const verifyToken = localStorage.getItem('token');
        const verifyUser = localStorage.getItem('user');
        console.log('🔍 Verification - Token stored:', !!verifyToken);
        console.log('🔍 Verification - User stored:', !!verifyUser);
        console.log('🔍 Stored token preview:', verifyToken ? verifyToken.substring(0, 20) + '...' : 'null');
        console.log('🔍 Stored user preview:', verifyUser ? JSON.parse(verifyUser).name : 'null');
        
        return {
          success: true,
          message: data.message || 'Login successful',
          token: data.token,
          user: data.user
        };
      } else {
        console.error('❌ Login failed - success was false');
        throw new Error(data.error || data.message || 'Login failed');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred during login');
    }
  }

  async register(userData: RegisterData): Promise<RegisterResponse> {
    try {
      console.log('Sending registration data:', userData);
      
      const response = await fetch(`${this.baseURL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      console.log('Registration response:', data);

      if (!response.ok) {
        const errorMessage = data.error || data.message || 'Registration failed';
        const errorDetails = data.details ? `\n${data.details.join('\n')}` : '';
        throw new Error(errorMessage + errorDetails);
      }

      // Store token and user data if registration successful
      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async checkAdminAccess(): Promise<AdminCheckResponse> {
    try {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          isAdmin: false,
          role: 'guest',
          permissions: {}
        };
      }

      const response = await fetch(`${this.baseURL}/check-admin`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check admin access');
      }

      return data;
    } catch (error) {
      console.error('Admin check error:', error);
      return {
        success: false,
        isAdmin: false,
        role: 'user',
        permissions: {}
      };
    }
  }

  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        await fetch(`${this.baseURL}/logout-enhanced`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken })
        });
      } else if (token) {
        await fetch(`${this.baseURL}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
      
      // Clear all tokens
      localStorage.removeItem('refreshToken');
      
      // Use session manager if available, otherwise fallback to localStorage
      if (this.sessionManager) {
        this.sessionManager.endSession();
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still remove local storage/session even if server call fails
      localStorage.removeItem('refreshToken');
      if (this.sessionManager) {
        this.sessionManager.endSession();
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }

  /**
   * Refresh the access token using the refresh token
   */
  async refreshAccessToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('No refresh token available');
        return false;
      }

      const response = await fetch(`${this.baseURL}/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update tokens
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        console.log('✅ Access token refreshed successfully');
        return true;
      } else {
        console.warn('❌ Failed to refresh token');
        // Clear invalid refresh token
        localStorage.removeItem('refreshToken');
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      localStorage.removeItem('refreshToken');
      return false;
    }
  }

  async getCurrentUser(): Promise<any> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }

      const response = await fetch(`${this.baseURL}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get current user');
      }

      const data = await response.json();
      return data.user;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // If session manager is available, use it for comprehensive validation
    if (this.sessionManager) {
      return !this.sessionManager.isTokenExpired(token) && !this.sessionManager.isSessionIdle();
    }

    // Fallback to basic token existence check
    return !!token;
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAdmin(): boolean {
    const user = this.getUser();
    return user && user.role === 'admin';
  }

  hasRole(roleName: string): boolean {
    const user = this.getUser();
    return user && user.role === roleName;
  }

  hasPermission(permission: string): boolean {
    const user = this.getUser();
    return user && user.permissions && user.permissions[permission] === true;
  }

  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  removeToken(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Get session information if session manager is available
   */
  getSessionInfo(): any {
    if (this.sessionManager) {
      return this.sessionManager.getSessionInfo();
    }
    return null;
  }

  /**
   * Extend the current session (refresh activity)
   */
  extendSession(): void {
    if (this.sessionManager) {
      this.sessionManager.extendSession();
    }
  }

  /**
   * Request password reset - sends temporary password to email
   */
  async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    try {
      const response = await fetch(`${this.baseURL}/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset request failed');
      }

      return data;
    } catch (error) {
      console.error('Password reset request error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * Change password (after forced password change)
   */
  async changePassword(currentPassword: string, newPassword: string, confirmNewPassword: string): Promise<ChangePasswordResponse> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${this.baseURL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmNewPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      // Update stored user to remove forcePasswordChange flag
      const user = this.getUser();
      if (user) {
        user.forcePasswordChange = false;
        localStorage.setItem('user', JSON.stringify(user));
      }

      return data;
    } catch (error) {
      console.error('Password change error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * Check if user needs to change password
   */
  needsPasswordChange(): boolean {
    const user = this.getUser();
    return user && user.forcePasswordChange === true;
  }
}

const authService = new AuthService();
export default authService; 
